import { useRoute } from "@react-navigation/native";
import { Home } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, View, XStack } from "tamagui";
import { Permission } from "../../permissions/types";
import { usePermissionCheck } from "../../permissions/useCheckPermission";
import { usePlanNavigation } from "../../plans/usePlanNavigation";
import { Body } from "../../ui/Body";
import type { IconProp } from "../../ui/icons";
import { Label } from "../../ui/Label";
import type { RootStackParamList } from "../../ui/navigation/Navigation";
import type { BusinessPlanModel } from "../plan/BusinessPlanModel";

type RouteName = keyof RootStackParamList;

type MenuItem<T extends RouteName> = {
  key: string;
  icon?: IconProp;
  title: string;
  routeName: RouteName;
  params: RootStackParamList[T];
};

export const BusinessPlanMenu: React.FC<{ businessPlan: BusinessPlanModel }> =
  observer((props) => {
    const navigation = usePlanNavigation();
    const visibleSections = props.businessPlan?.activeSections;
    const route = useRoute();
    const { checkPermission } = usePermissionCheck();

    const { t } = useTranslation();

    const menuItems = useMemo<MenuItem<RouteName>[]>(() => {
      const items: MenuItem<RouteName>[] = [
        {
          key: "home",
          icon: Home,
          title: t("business:menu_home"),
          routeName: "BusinessHome",
          params: {
            businessUid: props.businessPlan.uid,
          },
        },
        {
          key: "calendar",
          title: t("business:menu_calendar"),
          routeName: "BusinessSchedule",
          params: {
            businessUid: props.businessPlan.uid,
          },
        },
      ];

      if (
        props.businessPlan.totalTasksCount > 0 ||
        checkPermission(Permission.TasksAlwaysAccessible)
      ) {
        items.push({
          key: "tasks",
          title: t("business:menu_tasks"),
          routeName: "BusinessTasks",
          params: {
            businessUid: props.businessPlan.uid,
          },
        });
      }

      if (visibleSections?.length) {
        items.push({
          key: "info",
          title: t("business:menu_info"),
          routeName: "BusinessSection",
          params: {
            businessUid: props.businessPlan.uid,
          },
        });
      }

      items.push({
        key: "members",
        title: t("business:menu_members"),
        routeName: "BusinessMembers",
        params: {
          businessUid: props.businessPlan.uid,
        },
      });
      return items;
    }, [t, checkPermission, visibleSections, props.businessPlan]);

    return (
      <View position={"sticky" as "static"} zIndex={50} top={58}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          backgroundColor={"$backgroundBlock"}
          padding="$space.larger"
          borderTopColor={"$background"}
          borderTopWidth={1}
          borderBottomColor={"$background"}
          borderBottomWidth={1}
        >
          <XStack
            backgroundColor={"$backgroundBlock"}
            gap="$large"
            alignItems="center"
          >
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = route.name.includes(item.routeName);

              const TextComponent = isActive ? Label : Body;

              return (
                <XStack
                  key={item.key}
                  cursor="pointer"
                  alignItems="center"
                  gap="$small"
                  onPress={() => {
                    navigation.navigate(
                      // @ts-expect-error
                      item.routeName,
                      // biome-ignore lint/suspicious/noExplicitAny: Doesn't understand that it can't be undefined in our case
                      item.params as any,
                    );
                  }}
                >
                  {Icon && <Icon size={"$small"} />}
                  <TextComponent>{item.title}</TextComponent>
                </XStack>
              );
            })}
          </XStack>
        </ScrollView>
      </View>
    );
  });
